import React from 'react';
import { TrendingUp, MapPin, Calendar, ShieldCheck, Plus, Activity } from 'lucide-react';
import { apiService } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

const Sparkline = ({ data, width = 80, height = 32, growthPct = null }) => {
  const clean = Array.isArray(data)
    ? data.map((v) => Number(v)).filter((v) => Number.isFinite(v))
    : [];

  if (clean.length === 0) {
    return (
      <svg width={width} height={height} className="flex-shrink-0 opacity-60">
        <line x1="0" y1={height / 2} x2={width} y2={height / 2} stroke="#3f3f46" strokeWidth="1.5" strokeDasharray="4 3" />
      </svg>
    );
  }

  const series = clean.length === 1 ? [clean[0], clean[0]] : clean;
  const sorted = [...series].sort((a, b) => a - b);
  const q = (p) => {
    if (sorted.length === 0) return 0;
    const idx = Math.min(sorted.length - 1, Math.max(0, Math.floor((sorted.length - 1) * p)));
    return sorted[idx];
  };
  const low = q(0.1);
  const high = q(0.9);
  const clamped = series.map((v) => Math.max(low, Math.min(high, v)));
  const min = Math.min(...clamped);
  const max = Math.max(...clamped);
  const range = max - min || 1;
  const points = clamped.map((v, i) => {
    const x = (i / (clamped.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return `${x},${y}`;
  }).join(' ');
  const trend = clamped[clamped.length - 1] >= clamped[0];
  const color = trend ? '#22c55e' : '#ef4444';
  const growth = Number(growthPct);
  const growthLabel = Number.isFinite(growth)
    ? `${growth >= 0 ? '+' : ''}${growth.toFixed(0)}%`
    : 'n/a';
  const trendLabel = trend ? 'local uptrend' : 'local pullback';
  const title = `Mini trend: ${trendLabel}. Spike growth: ${growthLabel}.`;
  return (
    <svg width={width} height={height} className="flex-shrink-0" title={title}>
      <polyline fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" points={points} />
    </svg>
  );
};

const TopSpikesTableInner = ({ spikes, onEntityClick, onSpikesRefresh }) => {
  const [added, setAdded] = React.useState({});
  const [verifyingAi, setVerifyingAi] = React.useState({});
  const [hiddenByAi, setHiddenByAi] = React.useState({});
  const handleVerifyCasinoQuestion = async (e, item) => {
    e.stopPropagation();
    const entityId = item?.entity?.id;
    if (!entityId || verifyingAi[entityId]) return;
    setVerifyingAi(prev => ({ ...prev, [entityId]: true }));
    try {
      const res = await apiService.runAiFallbackVerifyEntity(entityId, true, true);
      const stats = res?.data?.stats || {};
      if (stats.should_hide_from_spikes || stats.new_status === 'not_gambling') {
        setHiddenByAi(prev => ({ ...prev, [entityId]: true }));
      }
      if (onSpikesRefresh) {
        await onSpikesRefresh();
      }
    } catch (err) {
      console.error('AI one-click verify failed:', err);
    } finally {
      setVerifyingAi(prev => ({ ...prev, [entityId]: false }));
    }
  };

  const reviewSourceLabels = React.useMemo(() => ({
    trustpilot: 'Trustpilot',
    reddit: 'Reddit',
    news_mention: 'News',
    serp_wikipedia: 'Wikipedia',
    social_media: 'Social',
  }), []);

  const getConfirmationMeta = (entity, hasSerp) => {
    const entityType = (entity?.entity_type || '').toLowerCase();
    const verification = (entity?.verification_status || '').toLowerCase();

    if (entityType === 'not_gambling') {
      return {
        label: 'Not Gambling',
        className: 'border-gray-300 text-gray-700 bg-muted',
        title: 'Classified as non-gambling/noise',
      };
    }
    if (entityType === 'offline_casino') {
      return {
        label: 'Offline',
        className: 'border-orange-300 text-orange-700',
        title: 'Offline/land-based casino',
      };
    }
    if (entityType === 'sportsbook') {
      return {
        label: 'Sportsbook',
        className: 'border-blue-300 text-blue-700',
        title: 'Sports betting brand',
      };
    }
    if (entityType === 'online_casino' && verification === 'verified_casino') {
      return {
        label: 'Casino confirmed',
        className: hasSerp ? 'border-emerald-300 text-emerald-700 bg-emerald-50' : 'border-emerald-300 text-emerald-700',
        title: 'Confirmed casino',
      };
    }
    if (entityType === 'online_casino' && verification === 'likely_casino') {
      return {
        label: 'Casino ?',
        className: hasSerp ? 'border-yellow-300 text-yellow-700 bg-yellow-50' : 'border-yellow-300 text-yellow-700',
        title: 'Likely casino, needs additional confirmation',
      };
    }
    return {
      label: 'No confirmation',
      className: 'border-gray-300 text-gray-700 bg-muted',
      title: 'There is no confirmation that this is a casino.',
    };
  };

  const getRiskMeta = (pbn) => {
    if (!pbn) return null;
    const npi = Number(pbn.npi || 0);
    const risk = String(pbn.risk_level || '').toLowerCase();
    if (!npi && !risk) return null;
    if (risk === 'high' || npi >= 8) {
      return { label: `High Risk${npi ? ` · NPI ${npi}` : ''}`, className: 'border-red-300 text-red-700 bg-red-50' };
    }
    if (risk === 'moderate' || npi >= 6) {
      return { label: `Caution${npi ? ` · NPI ${npi}` : ''}`, className: 'border-yellow-300 text-yellow-700 bg-yellow-50' };
    }
    return null;
  };

  const getTopDrivers = (signalBreakdown) => {
    if (!signalBreakdown) return [];
    const labels = {
      suggest: 'Suggest',
      trends: 'Trends',
      youtube: 'YouTube',
      reddit: 'Reddit',
      news: 'News',
      tiktok: 'TikTok',
      apps: 'Apps',
      yandex: 'Yandex',
    };
    return Object.entries(signalBreakdown)
      .filter(([, value]) => Number(value || 0) > 0)
      .sort((a, b) => Number(b[1] || 0) - Number(a[1] || 0))
      .slice(0, 1)
      .map(([key]) => labels[key] || key);
  };

  const getConfidenceScore = (item) => {
    const verification = String(item?.entity?.verification_status || '').toLowerCase();
    const entityType = String(item?.entity?.entity_type || '').toLowerCase();
    let score = 35;
    if (verification === 'verified_casino') score = 90;
    else if (verification === 'likely_casino') score = 68;
    else if (verification === 'not_gambling' || entityType === 'not_gambling') score = 8;
    else if (entityType === 'online_casino') score = 55;

    if (item?.has_serp) score += 8;
    if (item?.license?.has_license) score += 10;
    score += Math.max(0, Number(item?.confidence_multiplier || 1) - 1) * 6;
    return Math.max(0, Math.min(99, Math.round(score)));
  };

  const getFreshnessLabel = (dateStr) => {
    const dt = new Date(dateStr);
    if (!dt || Number.isNaN(dt.getTime())) return 'Freshness ?';
    const now = new Date();
    const days = Math.floor((now - dt) / (1000 * 60 * 60 * 24));
    if (days <= 0) return 'Fresh <24h';
    if (days === 1) return 'Fresh 1d';
    if (days <= 3) return `Fresh ${days}d`;
    return `Stale ${days}d`;
  };

  const buildWhyNow = (item, drivers) => {
    const parts = [];
    parts.push(`Pulse ${item.pulse ?? 0}`);
    parts.push(`Momentum ${(item.momentum || 0) > 0 ? '+' : ''}${item.momentum || 0}`);
    parts.push(`Breadth ${item.breadth ?? item.sources_active ?? 0}`);
    if ((item.spike?.growth_percentage || 0) > 0) {
      parts.push(`Growth +${Number(item.spike.growth_percentage || 0).toFixed(0)}%`);
    }
    if (drivers.length > 0) {
      parts.push(`Top drivers: ${drivers.join(', ')}`);
    }
    return parts.join(' | ');
  };
  
  const handleAddToWatchlist = async (e, entityId) => {
    e.stopPropagation();
    try {
      await apiService.addToWatchlist(entityId);
      setAdded(prev => ({ ...prev, [entityId]: true }));
    } catch (err) {
      console.error('Failed to add to watchlist:', err);
    }
  };

  const getSpikeTypeColor = (type) => {
    switch (type) {
      case 'sudden': return 'bg-accent/15 text-accent border-accent/30';
      case 'growth': return 'bg-chart-2/15 text-chart-2 border-chart-2/30';
      case 'anomaly': return 'bg-chart-1/15 text-chart-1 border-chart-1/30';
      case 'signal': return 'bg-sky-50 text-sky-700 border-sky-300';
      default: return 'bg-muted text-muted-foreground';
    }
  };
  
  const getSpikeTypeLabel = (type) => {
    switch (type) {
      case 'sudden': return 'Sudden Spike';
      case 'growth': return 'Growth';
      case 'anomaly': return 'Anomaly';
      case 'signal': return 'Signal';
      default: return type;
    }
  };
  
  if (!spikes || spikes.length === 0) {
    return (
      <Card className="card-elevated">
        <CardContent className="p-12 text-center">
          <ShieldCheck className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
          <p className="text-muted-foreground">No spikes detected yet</p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card className="card-elevated">
      <CardHeader>
        <CardTitle className="text-xl font-display flex items-center gap-2">
          <Activity className="w-5 h-5 text-accent" />
          Top Spikes
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {spikes.filter((item) => !hiddenByAi[item?.entity?.id]).map((item, index) => (
            (() => {
              const verification = (item.entity.verification_status || '').toLowerCase();
              const entityType = (item.entity.entity_type || '').toLowerCase();
              const isTrash = entityType === 'not_gambling';
              const isUnconfirmed = verification === 'unverified' || verification === '' || verification === 'unknown' || entityType === 'unknown';
              const hideScoring = isTrash || isUnconfirmed;
              const confirmationMeta = getConfirmationMeta(item.entity, item.has_serp);
              const riskMeta = getRiskMeta(item.pbn);
              const topDrivers = getTopDrivers(item.signal_breakdown);
              const allDrivers = Object.entries(item.signal_breakdown || {}).filter(([, v]) => Number(v || 0) > 0).length;
              const extraDrivers = Math.max(0, allDrivers - topDrivers.length);
              const confidenceScore = getConfidenceScore(item);
              const freshnessLabel = getFreshnessLabel(item.spike?.date);
              const whyNow = buildWhyNow(item, topDrivers);
              const compactMetaTitle = `${freshnessLabel} | ${whyNow}`;
              const sparklineData = Array.isArray(item.mini_timeline) && item.mini_timeline.length > 0
                ? item.mini_timeline
                : [Number(item.current_score || item.spike?.score || 0)];
              const regulators = item.regulators || [];
              const reviewSignals = item.review_signals || [];
              const reviewSignalsCount = Number(item.review_signals_count || 0);
              const isCasinoQuestion = confirmationMeta.label === 'Casino ?';
              const isVerifying = Boolean(verifyingAi[item.entity.id]);
              const reviewTooltip = reviewSignals.length > 0
                ? reviewSignals
                    .slice(0, 8)
                    .map((s) => `${reviewSourceLabels[s.source] || s.label || s.source}${s.detail ? `: ${s.detail}` : ''}`)
                    .join(' | ')
                : 'No review signals';
              return (
            <div
              key={index}
              className={`flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 rounded-lg border border-border bg-card transition-all cursor-pointer ${
                hideScoring
                  ? 'opacity-60 hover:border-border'
                  : 'hover:border-primary/50'
              }`}
              onClick={() => onEntityClick && onEntityClick(item.entity.id)}
            >
              <div className="flex-1 mb-3 sm:mb-0">
                <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                  <h3 className="font-display font-semibold text-foreground">
                    {item.entity.canonical_name}
                  </h3>
                  {!hideScoring && item.pulse > 0 && (
                    <span className={`text-sm font-bold px-1.5 py-0.5 rounded ${
                      item.pulse >= 80 ? 'bg-red-50 text-red-700' :
                      item.pulse >= 60 ? 'bg-emerald-50 text-emerald-700' :
                      item.pulse >= 40 ? 'bg-yellow-50 text-yellow-700' :
                      item.pulse >= 20 ? 'bg-blue-50 text-blue-700' :
                      'bg-muted text-muted-foreground'
                    }`} title="Brand Pulse">
                      {item.pulse}
                    </span>
                  )}
                  {hideScoring && (
                    <span className="text-xs font-semibold px-1.5 py-0.5 rounded bg-muted text-muted-foreground" title="There is no confirmation that this is a casino.">
                      ?
                    </span>
                  )}
                  {!hideScoring && (
                    <span
                      className={`text-xs font-semibold px-1.5 py-0.5 rounded ${
                        (item.momentum || 0) >= 1
                          ? 'bg-emerald-50 text-emerald-700'
                          : (item.momentum || 0) >= 0
                          ? 'bg-yellow-50 text-yellow-700'
                          : 'bg-red-50 text-red-700'
                      }`}
                      title={`Momentum: ${item.momentum || 0}`}
                    >
                      M {(item.momentum || 0) > 0 ? '+' : ''}{item.momentum || 0}
                    </span>
                  )}
                  {!hideScoring && (
                    <span
                      className={`text-xs font-semibold px-1.5 py-0.5 rounded ${
                        (item.breadth ?? item.sources_active ?? 0) >= 4
                          ? 'bg-emerald-50 text-emerald-700'
                          : (item.breadth ?? item.sources_active ?? 0) >= 3
                          ? 'bg-yellow-50 text-yellow-700'
                          : 'bg-muted text-muted-foreground'
                      }`}
                      title="Signal Breadth (active independent sources)"
                    >
                      B {item.breadth ?? item.sources_active ?? 0}
                    </span>
                  )}
                  {item.entity.official_name && <span className="text-[11px] text-blue-700 font-medium">({item.entity.official_name})</span>}
                  {item.entity.niche && item.entity.niche !== 'casino' && (
                    <Badge variant="outline" className="text-[11px] px-1.5 py-0 h-5 font-medium">
                      {item.entity.niche}
                    </Badge>
                  )}
                  {isCasinoQuestion ? (
                    <button
                      type="button"
                      onClick={(e) => handleVerifyCasinoQuestion(e, item)}
                      disabled={isVerifying}
                      className={`text-[11px] px-1.5 py-0 h-5 font-medium rounded border transition-colors ${confirmationMeta.className} ${
                        isVerifying ? 'opacity-70 cursor-wait' : 'hover:border-primary/60 hover:text-primary'
                      }`}
                      title={isVerifying ? 'Checking with GPT...' : 'Click to verify now with GPT'}
                    >
                      {isVerifying ? 'GPT...' : confirmationMeta.label}
                    </button>
                  ) : (
                    <Badge variant="outline" className={`text-[11px] px-1.5 py-0 h-5 font-medium ${confirmationMeta.className}`} title={confirmationMeta.title}>
                      {confirmationMeta.label}
                    </Badge>
                  )}
                  {isTrash && (
                    <Badge variant="outline" className="text-[11px] px-1.5 py-0 h-5 font-medium border-gray-300 text-gray-700 bg-muted">
                      Trash
                    </Badge>
                  )}
                  {!item.has_serp && (
                    <Badge variant="outline" className="text-[11px] px-1.5 py-0 h-5 font-medium border-gray-300 text-gray-700">no SERP</Badge>
                  )}
                  {riskMeta && (
                    <Badge variant="outline" className={`text-[11px] px-1.5 py-0 h-5 font-medium ${riskMeta.className}`}>
                      {riskMeta.label}
                    </Badge>
                  )}
                  {topDrivers.map((driver) => (
                    <Badge key={driver} variant="outline" className="text-[11px] px-1.5 py-0 h-5 font-medium border-sky-300 text-sky-700/80">
                      {driver}
                    </Badge>
                  ))}
                  {extraDrivers > 0 && (
                    <Badge variant="outline" className="text-[11px] px-1.5 py-0 h-5 font-medium border-sky-300 text-sky-700/70" title={`${extraDrivers} more active sources`}>
                      +{extraDrivers}
                    </Badge>
                  )}
                  <Badge
                    variant="outline"
                    className="text-[11px] px-1.5 py-0 h-5 font-medium border-indigo-300 text-indigo-700/80"
                    title={`Confidence score (verification + SERP + license + consistency) | ${compactMetaTitle}`}
                  >
                    C {confidenceScore}
                  </Badge>
                  {regulators.length > 0 && (
                    <Badge
                      variant="outline"
                      className="text-[11px] px-1.5 py-0 h-5 font-medium border-yellow-300 text-yellow-700"
                      title={regulators.join(', ')}
                    >
                      {regulators[0]}{regulators.length > 1 ? ` +${regulators.length - 1}` : ''}
                    </Badge>
                  )}
                  {reviewSignalsCount > 0 && (
                    <Badge
                      variant="outline"
                      className="text-[11px] px-1.5 py-0 h-5 font-medium border-sky-300 text-sky-700"
                      title={reviewTooltip}
                    >
                      R {reviewSignalsCount}
                    </Badge>
                  )}
                  {item.has_trustpilot && (
                    <Badge
                      variant="outline"
                      className="text-[11px] px-1.5 py-0 h-5 font-medium border-green-300 text-green-700"
                      title="Found on Trustpilot"
                    >
                      TP
                    </Badge>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                  {/* Два разных гео в одной строке раньше читались как ошибка фильтра:
                      слева регион спайка, справа регион первого обнаружения. Подписи
                      сделаны явными, чтобы "First seen: IT" в списке по CZ не выглядел
                      как утечка гео. */}
                  <span className="flex items-center gap-1" title="Region where this spike happened — matches the GEO filter">
                    <MapPin className="w-3 h-3" />
                    Spike: {item.spike.geo}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {new Date(item.spike.date).toLocaleDateString()}
                  </span>
                  <span className="text-[11px] text-muted-foreground font-medium" title={whyNow}>{freshnessLabel}</span>
                  {/* "First seen: IT" внутри списка по Анголе читалось как сломанный
                      фильтр. Это происхождение бренда, а не регион события, поэтому
                      подпись переименована и показывается только когда отличается
                      от региона спайка. */}
                  {item.entity.first_seen_geo && item.entity.first_seen_geo !== item.spike.geo && (
                    <span className="flex items-center gap-1 text-muted-foreground"
                          title="Where this brand was first ever detected. The row is in the list because the spike happened in the filtered region.">
                      Origin: {item.entity.first_seen_geo}
                    </span>
                  )}
                  {item.entity.official_domain && (
                    <a href={`https://${item.entity.official_domain}`} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1 text-blue-700 hover:text-blue-700 transition-colors"
                      onClick={e => e.stopPropagation()}>
                      {item.entity.official_domain}
                    </a>
                  )}
                  {Number(item.entity.mirrors_count || 0) > 0 && (
                    <span
                      className="flex items-center gap-1 text-[11px] text-amber-700"
                      title={`Зеркала той же сети: ${(item.entity.mirrors || []).slice(0, 8).join(', ')}${Number(item.entity.mirrors_count) > 8 ? ' и ещё ' + (Number(item.entity.mirrors_count) - 8) : ''}`}
                    >
                      {item.entity.mirrors_count} mirrors
                    </span>
                  )}
                  {item.license && item.license.has_license && (
                    <span className="flex items-center gap-1">
                      <span className="text-[11px] text-emerald-700">Licensed</span>
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                {/* Momentum -5 означает, что сигнал ИСЧЕЗ (текущая неделя пустая при
                    непустой предыдущей), а не отсутствие базы сравнения. Подписывать
                    такую строку как Sudden Spike нельзя: событие в прошлом, бренд угас. */}
                {(item.momentum || 0) <= -4 ? (
                  <Badge
                    className="bg-muted text-muted-foreground border-border"
                    title="Signal has died out: the current week is empty while the previous one was not. The spike itself is in the past."
                  >
                    Faded
                  </Badge>
                ) : (
                  <Badge className={getSpikeTypeColor(item.spike.spike_type)}>
                    {getSpikeTypeLabel(item.spike.spike_type)}
                  </Badge>
                )}
                {/* Source indicators */}
                {item.signal_breakdown && Object.keys(item.signal_breakdown).length > 0 && (
                  <div
                    className="flex items-center gap-0.5"
                    title={Object.entries(item.signal_breakdown || {})
                      .filter(([, v]) => Number(v || 0) > 0)
                      .map(([k, v]) => `${k}: ${v}`)
                      .join(' | ') || `${item.sources_active || 0}/8 sources active`}
                  >
                    {['suggest', 'trends', 'youtube', 'reddit', 'news'].map(src => {
                      const active = (item.signal_breakdown[src] || 0) > 0;
                      const labels = { suggest: 'S', trends: 'T', youtube: 'Y', reddit: 'R', news: 'N' };
                      const colors = { suggest: '#3b82f6', trends: '#22c55e', youtube: '#ef4444', reddit: '#f97316', news: '#8b5cf6' };
                      return (
                        <span
                          key={src}
                          className="text-[9px] font-bold w-4 h-4 rounded flex items-center justify-center"
                          style={{
                            backgroundColor: active ? colors[src] + '20' : 'transparent',
                            color: active ? colors[src] : '#444',
                            border: active ? `1px solid ${colors[src]}40` : '1px solid #333',
                          }}
                        >
                          {labels[src]}
                        </span>
                      );
                    })}
                  </div>
                )}
                <Sparkline data={sparklineData} growthPct={item.spike?.growth_percentage} />
                <div className="text-right">
                  {hideScoring ? (
                    <>
                      <div className="text-xs text-muted-foreground font-semibold">
                        There is no confirmation that this is a casino.
                      </div>
                      <p className="text-xs text-muted-foreground">Scoring hidden</p>
                    </>
                  ) : item.spike.spike_type === 'signal' ? (
                    <>
                      <div className="flex items-center gap-1 text-sky-700 font-semibold">
                        <Activity className="w-4 h-4" />
                        {item.current_score || item.spike.score?.toFixed(0)}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {item.confidence_multiplier > 1 ? `×${item.confidence_multiplier}` : 'Collecting'}
                      </p>
                    </>
                  ) : (
                    <>
                      {/* Процент на микробазе вводит в заблуждение: +200% может означать
                          рост с двух упоминаний до шести. Если база мала или неизвестна,
                          показываем процент приглушённо и говорим об этом в подсказке. */}
                      {(() => {
                        const base = item.spike.baseline;
                        const weakBase = base == null || base < 10;
                        return (
                          <div
                            className={`flex items-center gap-1 font-semibold ${weakBase ? 'text-muted-foreground' : 'text-success'}`}
                            title={weakBase
                              ? `Low comparison base${base != null ? ` (${base})` : ' (unknown)'} — the percentage is not reliable`
                              : `Grown from a base of ${base}`}
                          >
                            <TrendingUp className="w-4 h-4" />
                            +{item.spike.growth_percentage.toFixed(0)}%
                            {weakBase && <span className="text-[11px] font-normal">low base</span>}
                          </div>
                        );
                      })()}
                      <p className="text-xs text-muted-foreground">
                        {item.current_score || item.spike.score?.toFixed(0)}
                        {item.confidence_multiplier > 1 && (
                          <span
                            className="ml-1 text-blue-700"
                            title="Derived directly from the number of active sources (Breadth) — not an independent confirmation"
                          >×{item.confidence_multiplier}</span>
                        )}
                        {item.search_volume > 0 && (
                          <span className="ml-1 text-amber-700" title="Google Trends search volume">
                            {item.search_volume >= 1000000 ? `${(item.search_volume/1000000).toFixed(1)}M` : 
                               item.search_volume >= 1000 ? `${(item.search_volume/1000).toFixed(0)}K` : 
                               item.search_volume}
                          </span>
                        )}
                      </p>
                    </>
                  )}
                </div>
                <button
                  onClick={(e) => handleAddToWatchlist(e, item.entity.id)}
                  disabled={added[item.entity.id]}
                  className={`ml-2 p-1.5 rounded-md border transition-colors ${
                    added[item.entity.id]
                      ? 'border-emerald-300 text-emerald-700 bg-emerald-50'
                      : 'border-border text-muted-foreground hover:border-primary/50 hover:text-primary'
                  }`}
                  title={added[item.entity.id] ? 'Added to Cabinet' : 'Add to Cabinet'}
                >
                  <Plus className={`w-3.5 h-3.5 ${added[item.entity.id] ? 'rotate-45' : ''}`} />
                </button>
              </div>
            </div>
              );
            })()
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export const TopSpikesTable = React.memo(TopSpikesTableInner);

const PulseIcon = ({ className }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z"
    />
  </svg>
);
